import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Button, Icon, Toggle } from "pepsico-ds";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import ProductExclusionModal from "./ProductExclusionModal";
import {
  EVALUATION_WINDOW_TYPES,
  HISTORICAL_QUALIFICATION_OPTIONS,
  QUALIFICATION_METRICS,
  REVIEW_DATE_TYPES,
} from "./tierMockData";
import {
  getEvaluationWindowLabel,
  getHistoricalQualificationSummary,
} from "./tierEvaluationLabels";

// ----- Helper: deep-clone plain objects / arrays -----
function cloneDraft(tierRules) {
  if (!tierRules) return {};
  return JSON.parse(JSON.stringify(tierRules));
}

// ----- Generic Setting Modal -----
function SettingModal({ title, children, onClose, onApply, applyDisabled }) {
  return (
    <div className="setting-modal-overlay" onClick={onClose}>
      <div className="setting-modal" onClick={(e) => e.stopPropagation()}>
        <div className="setting-modal-header">
          <h3>{title}</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </div>
        <div className="setting-modal-body">{children}</div>
        <div className="setting-modal-footer">
          <Button size="medium" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={onApply}
            disabled={applyDisabled}
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----- Qualification Metric Modal -----
function QualificationMetricModal({ draft, onClose, onApply, marketCurrencyCode }) {
  const [metric, setMetric] = useState(draft?.qualificationMetric || "AMOUNT_SPENT");
  const currency = marketCurrencyCode || draft?.currencyCode || "";

  const isApplyDisabled = () => {
    if (metric === "AMOUNT_SPENT" && !currency.trim()) return true;
    return false;
  };

  const handleApply = () => {
    onApply({
      qualificationMetric: metric,
      currencyCode: currency.trim().toUpperCase(),
    });
  };

  return (
    <SettingModal
      title="Tier Qualification Metric"
      onClose={onClose}
      onApply={handleApply}
      applyDisabled={isApplyDisabled()}
    >
      <div className="select-label">
        <span>Qualification Metric</span>
        <span className="required-tag">*Required</span>
      </div>
      <div className="setting-radio-group">
        {QUALIFICATION_METRICS.map((opt) => (
          <label
            key={opt.value}
            className={`setting-radio-option ${opt.disabled ? "disabled" : ""}`}
          >
            <input
              type="radio"
              name="qualMetric"
              checked={metric === opt.value}
              onChange={() => setMetric(opt.value)}
              disabled={opt.disabled}
            />
            <div className="setting-radio-content">
              <div className="setting-radio-label">{opt.label}</div>
            </div>
          </label>
        ))}
      </div>

      {metric === "AMOUNT_SPENT" && (
        <div className="setting-inline-input" style={{ marginTop: 16 }}>
          <div className="select-label">
            <span>Currency Code</span>
            <span className="required-tag">*Required</span>
          </div>
          <input type="text" value={currency} disabled placeholder="e.g. USD" />
        </div>
      )}
    </SettingModal>
  );
}

// ----- Evaluation Window Modal -----
function EvaluationWindowModal({ draft, onClose, onApply }) {
  const [windowType, setWindowType] = useState(draft?.evaluationWindowType || "");
  const [months, setMonths] = useState(draft?.rollingWindowMonths || "");
  const [error, setError] = useState("");

  // Get the current qualification metric label
  const getQualificationMetricLabel = () => {
    const metric = draft?.qualificationMetric;
    if (!metric) return "Tier Qualification Metric";
    const metricOption = QUALIFICATION_METRICS.find((m) => m.value === metric);
    return metricOption ? metricOption.label : "Tier Qualification Metric";
  };

  const validateMonths = (value) => {
    if (!value) {
      setError("");
      return;
    }
    const num = Number(value);
    if (!Number.isInteger(num) || num <= 0) {
      setError("Custom window must be whole-number months");
    } else {
      setError("");
    }
  };

  const handleMonthsChange = (e) => {
    const val = e.target.value;
    setMonths(val);
    validateMonths(val);
  };

  const isApplyDisabled = () => {
    if (!windowType) return true;
    if (windowType === "ROLLING_X_MONTH_WINDOW") {
      const num = Number(months);
      return !months || !Number.isInteger(num) || num <= 0;
    }
    return false;
  };

  const handleApply = () => {
    const updates = { evaluationWindowType: windowType };
    if (windowType === "ROLLING_X_MONTH_WINDOW") {
      updates.rollingWindowMonths = Number(months);
    }
    onApply(updates);
  };

  return (
    <SettingModal
      title="Evaluation window"
      onClose={onClose}
      onApply={handleApply}
      applyDisabled={isApplyDisabled()}
    >
      <div className="select-label">
        <span>Select</span>
        <span className="required-tag">*Required</span>
      </div>
      <div className="setting-radio-group">
        {EVALUATION_WINDOW_TYPES.map((opt) => {
          // Replace placeholder with actual metric label
          const description = opt.description
            ? opt.description.replace(
                "<Tier Qualification Metric>",
                getQualificationMetricLabel()
              )
            : "";

          return (
            <label key={opt.value} className="setting-radio-option">
              <input
                type="radio"
                name="evalWindow"
                checked={windowType === opt.value}
                onChange={() => {
                  setWindowType(opt.value);
                  setError("");
                }}
              />
              <div className="setting-radio-content">
                <div className="setting-radio-label">{opt.label}</div>
                {description && (
                  <div className="setting-radio-desc">{description}</div>
                )}
              </div>
            </label>
          );
        })}
      </div>
      {windowType === "ROLLING_X_MONTH_WINDOW" && (
        <div className="setting-inline-input">
          <input
            type="number"
            value={months}
            onChange={handleMonthsChange}
            placeholder="Enter the number of months"
            min="1"
            step="1"
            className={error ? "input-has-error" : ""}
          />
          {error ? (
            <div className="input-error">
              <Icon icon="warning" size="small" /> {error}
            </div>
          ) : (
            <div className="input-hint">
              <Icon icon="info" size="small" /> Must be greater than 0, Whole months
              only
            </div>
          )}
        </div>
      )}
    </SettingModal>
  );
}

// ----- Review Date Schedule Modal -----
function ReviewDateModal({ draft, onClose, onApply }) {
  const currentSchedule = draft?.reviewDateSchedule;
  const [dateType, setDateType] = useState(currentSchedule?.type || "");
  const [fixedDate, setFixedDate] = useState(currentSchedule?.date || "");

  const selectedDate = useMemo(() => {
    if (!fixedDate) return null;
    const value = moment.utc(fixedDate, "YYYY-MM-DD", true);
    return value.isValid() ? value : null;
  }, [fixedDate]);

  const handleDateChange = (momentVal) => {
    if (!momentVal || !momentVal.isValid()) {
      setFixedDate("");
      return;
    }
    setFixedDate(momentVal.format("YYYY-MM-DD"));
  };

  const isApplyDisabled = () => {
    if (!dateType) return true;
    if (dateType === "FIXED_DATE") {
      return (
        !selectedDate || !selectedDate.isAfter(moment.utc().startOf("day"), "day")
      );
    }
    return false;
  };

  const handleApply = () => {
    onApply({
      reviewDateSchedule: {
        type: dateType,
        ...(dateType === "FIXED_DATE" ? { date: fixedDate } : {}),
      },
    });
  };

  return (
    <SettingModal
      title="Review Date Schedule"
      onClose={onClose}
      onApply={handleApply}
      applyDisabled={isApplyDisabled()}
    >
      <div className="select-label">
        <span>Select</span>
        <span className="required-tag">*Required</span>
      </div>
      <div className="setting-radio-group">
        {REVIEW_DATE_TYPES.map((opt) => (
          <label key={opt.value} className="setting-radio-option">
            <input
              type="radio"
              name="reviewDate"
              checked={dateType === opt.value}
              onChange={() => setDateType(opt.value)}
            />
            <div className="setting-radio-content">
              <div className="setting-radio-label">{opt.label}</div>
              {opt.description && (
                <div className="setting-radio-desc">{opt.description}</div>
              )}
            </div>
          </label>
        ))}
      </div>
      {dateType === "FIXED_DATE" && (
        <div className="review-date-picker-wrap">
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              minDate={moment.utc().add(1, "day").startOf("day")}
              format="MMM / DD / YYYY"
              className="customDatePickerStyles"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "MMM / DD / YYYY",
                  InputLabelProps: { shrink: false },
                },
              }}
            />
          </LocalizationProvider>
        </div>
      )}
    </SettingModal>
  );
}

// ----- Historical Qualification Mode Modal -----
function HistoricalQualificationModal({ draft, onClose, onApply }) {
  const [mode, setMode] = useState(draft?.historicalQualificationMode || "DISABLED");
  const [months, setMonths] = useState(draft?.historicalQualificationMonths ?? 12);
  const [error, setError] = useState("");

  const handleMonthsChange = (event) => {
    const value = event.target.value;
    setMonths(value);
    const parsed = Number(value);
    setError(
      value && Number.isInteger(parsed) && parsed > 0
        ? ""
        : "Historical period must be whole-number months"
    );
  };

  const handleApply = () => {
    onApply({
      historicalQualificationMode: mode,
      historicalQualificationMonths: mode === "ENABLED" ? Number(months) : null,
    });
  };

  const parsedMonths = Number(months);
  const applyDisabled =
    mode === "ENABLED" &&
    (!months || !Number.isInteger(parsedMonths) || parsedMonths <= 0);

  return (
    <SettingModal
      title="Historical Qualification Mode"
      onClose={onClose}
      onApply={handleApply}
      applyDisabled={applyDisabled}
    >
      <div className="select-label">
        <span>Select</span>
        <span className="required-tag">*Required</span>
      </div>
      <div className="setting-radio-group">
        {HISTORICAL_QUALIFICATION_OPTIONS.map((opt) => (
          <div key={opt.value} className="setting-radio-choice">
            <label className="setting-radio-option">
              <input
                type="radio"
                name="historicalQualification"
                checked={mode === opt.value}
                onChange={() => {
                  setMode(opt.value);
                  setError("");
                }}
              />
              <div className="setting-radio-content">
                <div className="setting-radio-label">{opt.label}</div>
                <div className="setting-radio-desc">{opt.description}</div>
              </div>
            </label>
            {opt.value === "ENABLED" && mode === "ENABLED" && (
              <div className="setting-inline-input">
                <div className="select-label">
                  <span>Historical transaction period (months)</span>
                  <span className="required-tag">*Required</span>
                </div>
                <input
                  type="number"
                  value={months}
                  onChange={handleMonthsChange}
                  min="1"
                  step="1"
                  aria-label="Historical transaction period in months"
                  className={error ? "input-has-error" : ""}
                />
                {error ? (
                  <div className="input-error">
                    <Icon icon="warning" size="small" /> {error}
                  </div>
                ) : (
                  <div className="input-hint">
                    <Icon icon="info" size="small" /> Defaults to 12 months. Whole
                    months only.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </SettingModal>
  );
}

// ----- Edit Pencil Icon -----
function EditPencilIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

// ----- Summary Card -----
function EvalSummaryCard({
  label,
  value,
  description,
  descriptionPrimary = false,
  onEdit,
}) {
  return (
    <div className="eval-model-card">
      <div className="eval-card-content">
        <div className="eval-card-label">{label}</div>
        <div className="eval-card-value">{value}</div>
        {description && (
          <div
            className={`eval-card-description${descriptionPrimary ? " primary" : ""}`}
          >
            {description}
          </div>
        )}
      </div>
      {onEdit && (
        <div className="eval-card-actions">
          <button className="edit-icon-btn" onClick={onEdit} title="Edit">
            <EditPencilIcon />
          </button>
        </div>
      )}
    </div>
  );
}

// ----- Main Component -----
export default function TierEvaluationModel({
  tierRules,
  onUpdate,
  saving,
  hasEditPermission = true,
}) {
  const { programMarketConfig } = useContext(GlobalConfigContext);
  const marketCurrencyCode = programMarketConfig?.currency?.code || "";
  const [openModal, setOpenModal] = useState(null);

  // Local draft state — mirrors tierRules until user edits via modals
  const [draft, setDraft] = useState(() => cloneDraft(tierRules));

  // Sync draft when tierRules changes from server (initial load / after save)
  useEffect(() => {
    setDraft(cloneDraft(tierRules));
  }, [tierRules]);

  // Check if draft differs from server state
  const isDirty = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(cloneDraft(tierRules));
  }, [draft, tierRules]);

  // Modal "Apply" — merges updates into local draft only
  const handleModalApply = useCallback((updates) => {
    setDraft((prev) => ({ ...prev, ...updates }));
    setOpenModal(null);
  }, []);

  // Cancel — reset draft back to server state
  const handleCancel = useCallback(() => {
    setDraft(cloneDraft(tierRules));
  }, [tierRules]);

  // Save — send entire draft to parent for a single GraphQL call
  const handleSave = useCallback(() => {
    onUpdate(draft);
  }, [draft, onUpdate]);

  // ----- Label helpers (read from draft so UI reflects pending changes) -----
  const getQualificationMetricLabel = () => {
    if (!draft?.qualificationMetric) return "Not configured";
    const metric = draft.qualificationMetric;
    const currency = marketCurrencyCode || draft.currencyCode || "";
    if (metric === "AMOUNT_SPENT")
      return `Amount Spent${currency ? ` - ${currency}` : ""}`;
    if (metric === "POINTS_EARNED") return "Points Earned";
    return metric;
  };

  const getReviewDateLabel = () => {
    if (!draft?.reviewDateSchedule) return "Not configured";
    const schedule = draft.reviewDateSchedule;
    if (schedule.type === "MEMBER_ENROLLMENT_ANNIVERSARY") {
      return "Member Enrollment Anniversary";
    }
    if (schedule.type === "FIXED_DATE") {
      const date = moment.utc(schedule.date, "YYYY-MM-DD", true);
      return date.isValid()
        ? `Fixed Date: ${date.format("MMMM D, YYYY")}`
        : "Not configured";
    }
    return schedule.type;
  };

  const getProductsExcludedLabel = () => {
    const excludedSkus = draft?.excludedSkus || [];
    const excludedGroups = draft?.excludedProductGroups || [];
    const totalExcluded = excludedSkus.length + excludedGroups.length;
    if (totalExcluded === 0) return "None";
    const parts = [];
    if (excludedGroups.length > 0)
      parts.push(`${excludedGroups.length} product group(s)`);
    if (excludedSkus.length > 0) parts.push(`${excludedSkus.length} product(s)`);
    return parts.join(", ");
  };

  const historicalQualification = getHistoricalQualificationSummary(draft);

  return (
    <div className="evaluation-model-section">
      <div className="eval-model-grid">
        {/* Qualification Metric */}
        <EvalSummaryCard
          label="Tier Qualification Metric"
          value={getQualificationMetricLabel()}
          onEdit={hasEditPermission ? () => setOpenModal("qualMetric") : undefined}
        />

        {/* Review Date Schedule */}
        <EvalSummaryCard
          label="Review Date Schedule"
          value={getReviewDateLabel()}
          onEdit={hasEditPermission ? () => setOpenModal("reviewDate") : undefined}
        />

        {/* Evaluation Window Type */}
        <EvalSummaryCard
          label="Evaluation Window Type"
          value={getEvaluationWindowLabel(draft)}
          onEdit={hasEditPermission ? () => setOpenModal("evalWindow") : undefined}
        />

        {/* Products Excluded */}
        <EvalSummaryCard
          label="Products Excluded from Tier Qualification"
          value={getProductsExcludedLabel()}
          onEdit={hasEditPermission ? () => setOpenModal("products") : undefined}
        />

        {/* Immediate Tier Downgrade - toggle card */}
        <div className="eval-model-card toggle-card">
          <div className="toggle-row">
            <div className="toggle-text">
              <div className="eval-card-label">Immediate Tier Downgrade</div>
              <div className="eval-card-value">
                Immediately downgrade the user if a qualifying transaction reduces
                their qualification metric.
              </div>
            </div>
            {hasEditPermission && (
              <div className="toggle-switch">
                <Toggle
                  checked={draft?.immediateDowngradeEnabled || false}
                  onUpdate={(checked) =>
                    setDraft((prev) => ({
                      ...prev,
                      immediateDowngradeEnabled: checked,
                    }))
                  }
                  size="medium"
                />
                <span className="toggle-status">
                  {draft?.immediateDowngradeEnabled ? "On" : "Off"}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Historical Qualification Mode */}
        <EvalSummaryCard
          label="Historical Qualification Mode"
          value={historicalQualification.value}
          description={historicalQualification.description}
          descriptionPrimary
          onEdit={
            hasEditPermission
              ? () => setOpenModal("historicalQualification")
              : undefined
          }
        />
      </div>

      {/* Save / Cancel Footer */}
      {hasEditPermission && isDirty && (
        <div className="eval-model-footer">
          <Button
            size="medium"
            variant="secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      )}

      {/* Modals — all read from draft, write back to draft only */}
      {openModal === "qualMetric" && (
        <QualificationMetricModal
          draft={draft}
          onClose={() => setOpenModal(null)}
          onApply={handleModalApply}
          marketCurrencyCode={marketCurrencyCode}
        />
      )}
      {openModal === "evalWindow" && (
        <EvaluationWindowModal
          draft={draft}
          onClose={() => setOpenModal(null)}
          onApply={handleModalApply}
        />
      )}
      {openModal === "reviewDate" && (
        <ReviewDateModal
          draft={draft}
          onClose={() => setOpenModal(null)}
          onApply={handleModalApply}
        />
      )}
      {openModal === "historicalQualification" && (
        <HistoricalQualificationModal
          draft={draft}
          onClose={() => setOpenModal(null)}
          onApply={handleModalApply}
        />
      )}
      {openModal === "products" && (
        <ProductExclusionModal
          tierRules={draft}
          onClose={() => setOpenModal(null)}
          onApply={handleModalApply}
        />
      )}
    </div>
  );
}
